import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FilmStrip, CaretRight, Star, Play } from '@phosphor-icons/react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const HERO_IMAGE = 'https://images.unsplash.com/photo-1518676590629-3dcba9c5a555?w=1920&q=90';

const ThemeFestival = ({ user }) => {
  const navigate = useNavigate();
  const [movies, setMovies] = useState([]);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/movies`).then((r) => setMovies(r.data || [])).catch(() => {});
  }, []);

  const dashRoute = () => {
    if (user?.role === 'admin') return '/admin-dashboard';
    if (user?.role === 'producer') return '/producer-dashboard';
    if (user?.role === 'fan') return '/fan-dashboard';
    return '/investor-dashboard';
  };

  return (
    <div className="min-h-screen text-[#f5f1e6]" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div
        className="fixed inset-0 -z-10 bg-cover bg-center"
        style={{
          backgroundImage: `url(${HERO_IMAGE})`,
          filter: 'saturate(0.85) brightness(0.7) hue-rotate(-10deg)',
        }}
      />
      <div
        className="fixed inset-0 -z-10"
        style={{
          background:
            'linear-gradient(135deg, rgba(8,15,30,0.85) 0%, rgba(15,30,55,0.85) 50%, rgba(8,15,30,0.95) 100%)',
        }}
      />
      <div
        className="fixed inset-0 -z-10 opacity-30"
        style={{
          background:
            'radial-gradient(ellipse at 50% 30%, rgba(212,175,55,0.25) 0%, transparent 60%)',
        }}
      />

      <nav data-web-chrome="nav" className="relative z-10 px-8 py-6 max-w-7xl mx-auto flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img src="/fanzingy-logo.jpeg" alt="FanZingy" className="h-10 w-10 rounded-full object-cover ring-1 ring-[#D4AF37]/40" data-testid="brand-logo" />
          <span
            className="text-2xl tracking-[0.2em]"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 600, color: '#D4AF37' }}
          >
            FanZingy
          </span>
        </div>
        <div className="flex items-center gap-6">
          <button
            data-testid="theme-festival-marketplace"
            onClick={() => navigate('/marketplace')}
            className="text-sm tracking-widest text-[#f5f1e6]/70 hover:text-[#D4AF37] uppercase font-light transition-colors"
          >
            Catalog
          </button>
          <button
            onClick={() => navigate('/community')}
            className="text-sm tracking-widest text-[#f5f1e6]/70 hover:text-[#D4AF37] uppercase font-light transition-colors"
          >
            Salon
          </button>
          {user ? (
            <button
              onClick={() => navigate(dashRoute())}
              className="px-7 py-2.5 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a1020] rounded-none text-xs tracking-[0.25em] uppercase font-semibold transition-all"
            >
              Patron Portal
            </button>
          ) : (
            <button
              onClick={() => navigate('/login')}
              className="px-7 py-2.5 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0a1020] rounded-none text-xs tracking-[0.25em] uppercase font-semibold transition-all"
            >
              Enter
            </button>
          )}
        </div>
      </nav>

      <section className="relative z-10 max-w-5xl mx-auto px-8 pt-28 pb-32 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1 }}
        >
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="h-px w-12 bg-[#D4AF37]/60" />
            <Star size={14} weight="fill" className="text-[#D4AF37]" />
            <div className="h-px w-12 bg-[#D4AF37]/60" />
          </div>
          <p
            style={{ fontFamily: "'Playfair Display', serif" }}
            className="text-sm tracking-[0.5em] text-[#D4AF37] mb-8 uppercase italic"
          >
            Invitation Only · Est. 2026
          </p>
          <h1
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
            className="text-5xl md:text-7xl mb-8 leading-tight italic"
          >
            The art of cinema,
            <br />
            <span className="font-bold not-italic text-[#D4AF37]">the science of return.</span>
          </h1>
          <p className="text-lg text-[#f5f1e6]/70 mb-12 max-w-2xl mx-auto leading-loose font-light tracking-wide">
            A curated marketplace where discerning patrons fund tomorrow's masterpieces, earning royalties as cinema unfolds.
          </p>
          <div className="flex flex-wrap gap-5 justify-center items-center">
            <button
              onClick={() => navigate('/register')}
              className="group px-12 py-4 bg-[#D4AF37] text-[#0a1020] rounded-none text-sm tracking-[0.25em] uppercase font-bold hover:bg-[#f5d76e] transition-all flex items-center gap-3"
            >
              Request Access
              <CaretRight size={14} weight="bold" className="group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              onClick={() => navigate('/marketplace')}
              className="px-12 py-4 border border-[#f5f1e6]/30 text-[#f5f1e6] rounded-none text-sm tracking-[0.25em] uppercase font-light hover:border-[#D4AF37] hover:text-[#D4AF37] transition-all flex items-center gap-3"
            >
              <Play size={12} weight="fill" /> Preview
            </button>
          </div>
        </motion.div>
      </section>

      {movies.length > 0 && (
        <section className="relative z-10 max-w-6xl mx-auto px-8 pb-24">
          <div className="text-center mb-16">
            <div className="flex items-center justify-center gap-2 mb-4">
              <div className="h-px w-8 bg-[#D4AF37]/60" />
              <span className="text-xs tracking-[0.4em] text-[#D4AF37] uppercase">Featured Productions</span>
              <div className="h-px w-8 bg-[#D4AF37]/60" />
            </div>
            <h2
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 500 }}
              className="text-4xl md:text-5xl italic"
            >
              In Production
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {movies.slice(0, 3).map((m) => (
              <motion.button
                key={m.id}
                whileHover={{ y: -10 }}
                onClick={() => navigate(`/movie/${m.id}`)}
                className="text-left group"
              >
                <div className="relative aspect-[2/3] rounded-none overflow-hidden mb-5 border border-[#D4AF37]/20 group-hover:border-[#D4AF37]/60 transition-colors">
                  <img src={m.poster_url} alt={m.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0a1020]/80 via-transparent to-transparent" />
                </div>
                <p className="text-[10px] tracking-[0.4em] text-[#D4AF37] uppercase mb-2">{m.industry}</p>
                <h3
                  style={{ fontFamily: "'Playfair Display', serif" }}
                  className="text-2xl italic group-hover:text-[#D4AF37] transition-colors mb-2"
                >
                  {m.title}
                </h3>
                <p className="text-sm text-[#f5f1e6]/60 font-light line-clamp-2 leading-relaxed">{m.description}</p>
              </motion.button>
            ))}
          </div>
        </section>
      )}

      <footer className="relative z-10 border-t border-[#D4AF37]/15 py-10 text-center">
        <p
          style={{ fontFamily: "'Playfair Display', serif" }}
          className="text-xs text-[#f5f1e6]/40 tracking-[0.3em] italic uppercase"
        >
          © {new Date().getFullYear()} Fanzingy · Festival Prestige Theme
        </p>
      </footer>
    </div>
  );
};

export default ThemeFestival;
